import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { LoadingSpinner } from "../LoadingSpinner";
import { API_CONSTANTS, AUDIO_MUTE, TTS_CONFIG, ASSET_URLS } from "../constants";
import {
  extractCompositeGameJobId,
  extractVoiceIdAndToken,
  getAudioCacheKey,
  getMuteStorageKey,
} from "../utils";

interface GameAudioResponse {
  text: string;
  audioUrl: string;
}

interface AudioUrlsApiResponse {
  result: {
    status: number;
    message: string;
    response: {
      gameAudioResponse: GameAudioResponse[];
    };
  };
}

interface GameAudioContextType {
  kinzySpeak: (text: string) => Promise<void>;
  isPlaying: boolean;
  stopAudio: () => void;
  startBackgroundMusic: () => void;
  stopBackgroundMusic: () => void;
  isBackgroundMusicPlaying: boolean;
}

interface GameAudioProviderProps {
  children: React.ReactNode;
  audioVolume?: number;
  apiBaseUrl?: string;
}

const GameAudioContext = createContext<GameAudioContextType | null>(null);

const buildTextToUrlMap = (
  apiResponse: AudioUrlsApiResponse
): Map<string, string> => {
  const textToUrlMap = new Map<string, string>();
  console.log("[cache-init] Processing audio URLs from API response...");

  apiResponse.result.response.gameAudioResponse.forEach(
    ({ text, audioUrl }, index) => {
      const cacheKey = getAudioCacheKey(text);
      textToUrlMap.set(cacheKey, audioUrl);

      console.log(
        `[cache-init] Entry ${
          index + 1
        }: Text: "${text}" → Cache Key: "${cacheKey}" → URL: "${audioUrl}"`
      );
    }
  );

  console.log(
    `[cache-init] Successfully processed ${textToUrlMap.size} audio URL mappings`
  );
  return textToUrlMap;
};

const MuteButton: React.FC<{ isMuted: boolean; toggleMute: () => void }> = ({
  isMuted,
  toggleMute,
}) => {
  return (
    <button
      onClick={toggleMute}
      className={`${AUDIO_MUTE.BUTTON_SIZE} ${AUDIO_MUTE.BUTTON_POSITION} bg-transparent hover:bg-white/10 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center rounded-lg`}
      aria-label={isMuted ? "Unmute audio" : "Mute audio"}
      title={isMuted ? "Unmute audio" : "Mute audio"}
    >
      <img
        src={isMuted ? ASSET_URLS.MUTE_BUTTON : ASSET_URLS.UNMUTE_BUTTON}
        alt={isMuted ? "Muted" : "Unmuted"}
        className="w-full h-full object-contain p-1"
      />
    </button>
  );
};

export const GameAudioProvider: React.FC<GameAudioProviderProps> = ({
  children,
  audioVolume = TTS_CONFIG.AUDIO_VOLUME,
  apiBaseUrl = API_CONSTANTS.BASE_URL,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBackgroundMusicPlaying, setIsBackgroundMusicPlaying] = useState(false);

  const audioCacheRef = useRef(new Map<string, string>());
  const textToAudioElementMapRef = useRef(new Map<string, HTMLAudioElement>());
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const backgroundMusicRef = useRef<HTMLAudioElement | null>(null);
  const processedTextsRef = useRef(new Set<string>());

  const compositeGameJobId = useMemo(() => extractCompositeGameJobId(), []);
  const { voiceId, token } = useMemo(() => extractVoiceIdAndToken(), []);
  const mode = useMemo(() => (token ? "admin" : "user"), [token]);

  const [isMuted, setIsMutedState] = useState<boolean>(() => {
    const muteStorageKey = getMuteStorageKey(compositeGameJobId);
    if (!muteStorageKey) return AUDIO_MUTE.DEFAULT_MUTED;
    localStorage.removeItem(muteStorageKey);
    console.log(
      `${AUDIO_MUTE.LOG_PREFIX} Cleared mute state for game: ${compositeGameJobId}`
    );
    return AUDIO_MUTE.DEFAULT_MUTED;
  });

  const setMuted = useCallback(
    (muted: boolean) => {
      console.log(
        `[mute] Changing mute state from ${isMuted} to ${muted} for game: "${compositeGameJobId}"`
      );
      setIsMutedState(muted);

      if (currentAudioRef.current) {
        currentAudioRef.current.muted = muted;
        console.log(
          `[mute] Applied mute state ${muted} to currently playing audio`
        );
      } else {
        console.log(`[mute] No currently playing audio to apply mute state to`);
      }

      if (backgroundMusicRef.current) {
        backgroundMusicRef.current.muted = muted;
        console.log(`[mute] Applied mute state ${muted} to background music`);
      }

      const muteStorageKey = getMuteStorageKey(compositeGameJobId);
      if (muteStorageKey) {
        localStorage.setItem(muteStorageKey, JSON.stringify(muted));
        console.log(
          `[mute] Persisted mute state ${muted} to localStorage with key: "${muteStorageKey}"`
        );
      } else {
        console.log(
          `[mute] Could not persist mute state - no storage key available`
        );
      }
    },
    [compositeGameJobId, isMuted]
  );

  const toggleMute = useCallback(() => {
    setMuted(!isMuted);
  }, [isMuted, setMuted]);

  const stop = useCallback(() => {
    console.log(
      `[playback] Stop called - checking for currently playing audio`
    );
    if (currentAudioRef.current) {
      console.log(`[playback] Stopping and resetting currently playing audio`);
      if (!currentAudioRef.current.paused) {
        currentAudioRef.current.pause();
      }
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    } else {
      console.log(`[playback] No currently playing audio to stop`);
    }
    setIsPlaying(false);
    console.log(`[playback] Set isPlaying to false`);
  }, []);

  const populateCache = useCallback(
    async (jobId: string): Promise<void> => {
      try {
        console.log(
          `[cache-init] Starting audio cache population for game: "${jobId}"`
        );

        const apiUrl = `${apiBaseUrl}/api/v1/games/audio-urls?compositeGameJobId=${encodeURIComponent(
          jobId
        )}&voiceId=${encodeURIComponent(voiceId || "")}`;
        console.log(`[cache-init] Making GET request to: "${apiUrl}"`);

        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error(`API responded with status: ${response.status}`);
        }

        const data: AudioUrlsApiResponse = await response.json();
        if (data.result?.status !== 200) {
          throw new Error(data.result?.message || "Unknown API error");
        }

        console.log(
          `[cache-init] API response received with ${data.result.response.gameAudioResponse.length} audio entries`
        );

        const textToUrlMap = buildTextToUrlMap(data);
        audioCacheRef.current = textToUrlMap;

        console.log(
          `[cache-init] Audio cache populated successfully with ${textToUrlMap.size} mappings`
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        console.error(
          `[cache-init] Failed to populate cache for game "${jobId}": ${errorMessage}`,
          err
        );
        toast.error(`Audio cache error: ${errorMessage}`);
        throw err;
      }
    },
    [apiBaseUrl, voiceId]
  );

  const generateAdminTTS = useCallback(
    async (text: string): Promise<string | null> => {
      if (!voiceId || !token) {
        console.error("[admin] Missing voiceId or token for TTS generation");
        return null;
      }

      try {
        console.log(`[admin] Requesting TTS generation for text: "${text}"`);
        console.log(
          `[admin] Using voiceId: "${voiceId}" for game: "${compositeGameJobId}"`
        );

        const requestBody = {
          text,
          voiceId,
          compositeGameJobId,
        };

        console.log(`[admin] POST request body:`, requestBody);

        const response = await fetch(
          `${apiBaseUrl}/api/v1/games/tts/generate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error(`TTS API returned an error: ${response.status}`);
        }

        const data = await response.json();
        const audioUrl = data?.result?.response?.audioUrl;

        if (!audioUrl) {
          throw new Error("Audio URL not found in TTS API response");
        }

        console.log(
          `[admin] TTS generation successful for text: "${text}" → Generated URL: "${audioUrl}"`
        );
        return audioUrl;
      } catch (error) {
        console.error(
          `[admin] TTS generation failed for text: "${text}" - Error:`,
          error
        );
        toast.error("Failed to generate audio");
        return null;
      }
    },
    [voiceId, token, compositeGameJobId, apiBaseUrl]
  );

  const playAudio = useCallback(
    async (text: string, audioUrl: string): Promise<boolean> => {
      const cacheKey = getAudioCacheKey(text);
      console.log(
        `[playback] Starting playback for text: "${text}" → Cache Key: "${cacheKey}" → URL: "${audioUrl}"`
      );

      stop();

      setIsPlaying(true);

      try {
        let audioHtmlElement = textToAudioElementMapRef.current.get(cacheKey);
        if (!audioHtmlElement) {
          console.log(
            `[playback] Creating new Audio element for cache key: "${cacheKey}"`
          );
          audioHtmlElement = new Audio(audioUrl);
          audioHtmlElement.volume = audioVolume;
          audioHtmlElement.muted = isMuted;
          audioHtmlElement.addEventListener(
            "ended",
            () => {
              console.log(`[playback] Audio ended for text: "${text}"`);
              if (currentAudioRef.current === audioHtmlElement) {
                setIsPlaying(false);
                currentAudioRef.current = null;
              }
            },
            { once: true }
          );

          audioHtmlElement.addEventListener("error", (e) => {
            console.error(`[playback] Audio error for text: "${text}"`, e);
            setIsPlaying(false);
            currentAudioRef.current = null;
          });
          textToAudioElementMapRef.current.set(cacheKey, audioHtmlElement);
          console.log(
            `[playback] Audio element created with volume: ${audioVolume}, muted: ${isMuted}`
          );
        } else {
          console.log(
            `[playback] Reusing existing Audio element for cache key: "${cacheKey}"`
          );
          audioHtmlElement.muted = isMuted;
          console.log(`[playback] Updated mute state to: ${isMuted}`);
        }

        currentAudioRef.current = audioHtmlElement;
        audioHtmlElement.currentTime = 0;

        console.log(`[playback] Starting audio playback for text: "${text}"`);
        await audioHtmlElement.play();

        console.log(
          `[playback] Successfully started playing: "${text}" from URL: "${audioUrl}"`
        );
        return true;
      } catch (error) {
        console.error(
          `[playback] Playback failed for text: "${text}" - Error:`,
          error
        );

        if (error instanceof Error && error.name !== "AbortError") {
          toast.error("Failed to play audio");
        } else if (error instanceof Error && error.name === "AbortError") {
          console.log(
            `[playback] Audio play was aborted (likely due to new audio starting) - this is normal`
          );
        } else {
          console.log(`[playback] Unknown error type:`, error);
        }

        setIsPlaying(false);
        currentAudioRef.current = null;
        return false;
      }
    },
    [audioVolume, isMuted, stop]
  );

  const kinzySpeak = useCallback(
    async (text: string): Promise<void> => {
      if (!text?.trim()) {
        console.warn(
          "[kinzySpeak] Empty or whitespace-only text provided - ignoring call"
        );
        return;
      }

      const cacheKey = getAudioCacheKey(text);
      console.log(
        `[kinzySpeak] Processing request for text: "${text}" → Cache Key: "${cacheKey}" (Mode: ${mode})`
      );

      let audioUrl = audioCacheRef.current.get(cacheKey);

      if (audioUrl) {
        console.log(
          `[cache-hit] Found cached URL for text: "${text}" → URL: "${audioUrl}"`
        );
        await playAudio(text, audioUrl);
        return;
      }

      console.log(
        `[cache-miss] No cached URL found for text: "${text}" (Cache Key: "${cacheKey}")`
      );

      if (mode === "admin") {
        if (processedTextsRef.current.has(cacheKey)) {
          console.log(
            `[admin] Skipping duplicate API call for cache key: "${cacheKey}" (Text: "${text}")`
          );
          return;
        }

        console.log(
          `[admin] Marking cache key as processed: "${cacheKey}" (Text: "${text}")`
        );
        processedTextsRef.current.add(cacheKey);
        const generatedUrl = await generateAdminTTS(text);

        if (generatedUrl) {
          audioUrl = generatedUrl;
          console.log(
            `[admin] Adding to cache: "${text}" → Cache Key: "${cacheKey}" → URL: "${audioUrl}"`
          );
          audioCacheRef.current.set(cacheKey, audioUrl);
          await playAudio(text, audioUrl);
        } else {
          console.log(
            `[admin] TTS generation failed for text: "${text}" - removing from processed set`
          );
          processedTextsRef.current.delete(cacheKey);
        }
      } else {
        console.log(
          `[user] No cached URL available for text: "${text}" (Cache Key: "${cacheKey}") - failing silently`
        );
      }
    },
    [mode, playAudio, generateAdminTTS]
  );

  const startBackgroundMusic = useCallback(() => {
    if (!backgroundMusicRef.current) {
      console.log("[background-music] Initializing background music");
      const backgroundMusic = new Audio(
        "https://kinzy.club/games/background-sounds/background_music.mp3"
      );
      backgroundMusic.loop = true;
      backgroundMusic.volume = 0.3;
      backgroundMusic.muted = isMuted;
      backgroundMusicRef.current = backgroundMusic;
    }

    const backgroundMusic = backgroundMusicRef.current;

    if (backgroundMusic.paused) {
      console.log("[background-music] Starting background music");
      backgroundMusic
        .play()
        .then(() => {
          console.log("[background-music] Successfully started");
          setIsBackgroundMusicPlaying(true);
        })
        .catch((error) => {
          console.error("[background-music] Failed to start:", error);
        });
    }
  }, [isMuted]);

  const stopBackgroundMusic = useCallback(() => {
    if (backgroundMusicRef.current && !backgroundMusicRef.current.paused) {
      console.log("[background-music] Stopping background music");
      backgroundMusicRef.current.pause();
      backgroundMusicRef.current.currentTime = 0;
      setIsBackgroundMusicPlaying(false);
    }
  }, []);

  useEffect(() => {
    const backgroundMusic = new Audio(
      "https://kinzy.club/games/background-sounds/background_music.mp3"
    );
    backgroundMusic.loop = true;
    backgroundMusic.volume = 0.3;
    backgroundMusic.muted = isMuted;
    backgroundMusicRef.current = backgroundMusic;

    console.log(
      `[background-music] Initialized background music (volume: ${backgroundMusic.volume}, will start on user action)`
    );

    return () => {
      console.log("[background-music] Cleaning up background music");
      if (backgroundMusicRef.current) {
        backgroundMusicRef.current.pause();
        backgroundMusicRef.current.src = "";
        backgroundMusicRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const initializeCache = async () => {
      if (!compositeGameJobId) {
        console.log(
          "[initialization] No composite game job ID found - skipping cache initialization"
        );
        setIsLoading(false);
        return;
      }

      console.log(
        `[initialization] Starting GameAudioProvider initialization for game: "${compositeGameJobId}"`
      );
      console.log(
        `[initialization] Mode detected: ${mode} (Token present: ${!!token}, VoiceId present: ${!!voiceId})`
      );

      try {
        await populateCache(compositeGameJobId);
        console.log(
          `[initialization] GameAudioProvider initialization completed successfully for game: "${compositeGameJobId}"`
        );
      } catch (error) {
        console.error(
          `[initialization] GameAudioProvider initialization failed for game: "${compositeGameJobId}"`,
          error
        );
      } finally {
        setIsLoading(false);
        console.log(
          `[initialization] Loading state set to false for game: "${compositeGameJobId}"`
        );
      }
    };

    void initializeCache();
  }, [compositeGameJobId, populateCache, mode, token, voiceId]);

  useEffect(() => {
    return () => {
      console.log(
        `[cleanup] GameAudioProvider unmounting - cleaning up resources`
      );
      stop();
      const audioElementCount = textToAudioElementMapRef.current.size;
      const processedTextCount = processedTextsRef.current.size;

      textToAudioElementMapRef.current.clear();
      processedTextsRef.current.clear();

      console.log(
        `[cleanup] Cleared ${audioElementCount} audio elements and ${processedTextCount} processed text entries`
      );
    };
  }, [stop]);

  const contextValue: GameAudioContextType = useMemo(
    () => ({
      kinzySpeak,
      isPlaying,
      stopAudio: stop,
      startBackgroundMusic,
      stopBackgroundMusic,
      isBackgroundMusicPlaying,
    }),
    [
      kinzySpeak,
      isPlaying,
      stop,
      startBackgroundMusic,
      stopBackgroundMusic,
      isBackgroundMusicPlaying,
    ]
  );

  if (isLoading) {
    return <LoadingSpinner message="Loading audio cache..." />;
  }

  return (
    <GameAudioContext.Provider value={contextValue}>
      {children}
      <MuteButton isMuted={isMuted} toggleMute={toggleMute} />
    </GameAudioContext.Provider>
  );
};

export const useGameAudio = (): GameAudioContextType => {
  const context = useContext(GameAudioContext);
  if (!context) {
    throw new Error("useGameAudio must be used within GameAudioProvider");
  }
  return context;
};

export type {
  AudioUrlsApiResponse,
  GameAudioContextType,
  GameAudioProviderProps,
  GameAudioResponse,
};